import React, { FC, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Spinner } from 'src/components/spinner';
import { NavigationService } from 'src/services/navigation-service';
import { selectHasAccountBeenSelected } from 'src/state/account/selectors';
import { startAuthenticationActionCreator } from 'src/state/auth/action-creators/start-authentication-action-creator';
import { selectAuthFailed, selectIsAuthenticated, selectIsAuthenticating } from 'src/state/auth/selectors';
import { store } from 'src/state/configure-store';
import { selectHasOrderProcessStartedOrCompleted } from 'src/state/order/selectors';
import { Requests } from 'src/state/request/request-action';
import { selectIsShopEnabled, selectRequestsState } from 'src/state/request/selectors';
import { RequestStatus, RequestType } from 'src/state/request/types';
import { useAppDispatch, useAppSelector } from 'src/state/thunk-api';

import { GuardForRoute } from './guard-for-route';
import { GuardForRouteService } from './guard-for-route-service';
import { RequestRoute, RequestRouteProps } from './request-route';

interface OwnProps {
  initialGuards?: GuardForRoute[];
}

type Props = OwnProps & RequestRouteProps;

export const ProtectedOrderRoute: FC<Props> = (props) => {
  const { initialGuards, ...rest } = props;
  const authenticated = useAppSelector(selectIsAuthenticated);
  const authorizing = useAppSelector(selectIsAuthenticating);
  const accountSelected = useAppSelector(selectHasAccountBeenSelected);
  const authHasFailed = useAppSelector(selectAuthFailed);
  const requestsState = useAppSelector(selectRequestsState);
  const { pathname } = useLocation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!authenticated && !authorizing && !authHasFailed) {
      dispatch(startAuthenticationActionCreator());
    }
  }, [authenticated, authorizing, authHasFailed, dispatch]);

  useEffect(
    () => {
      if (authenticated && requestsState[RequestType.GET_SHOP_STATUS].status !== RequestStatus.SUCCESS) {
        dispatch(Requests[RequestType.GET_SHOP_STATUS]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authenticated],
  );

  if (authHasFailed) {
    return <Navigate to={NavigationService.ERROR} replace={true} />;
  }

  if (
    !authenticated ||
    (accountSelected && authorizing) ||
    requestsState[RequestType.GET_SHOP_STATUS].status !== RequestStatus.SUCCESS
  ) {
    return <Spinner />;
  }

  if (selectHasOrderProcessStartedOrCompleted(store.getState()) && pathname !== NavigationService.SUCCESS) {
    return <Navigate to={NavigationService.SUCCESS} replace={true} />;
  }

  if (!selectIsShopEnabled(store.getState())) {
    return <Navigate to={NavigationService.ERROR_PRODUCT_NOT_AVAILABLE} replace={true} />;
  }

  if (initialGuards && GuardForRouteService.isProtectedRoute(initialGuards)) {
    return <Navigate to={GuardForRouteService.getRedirectUrl(initialGuards)} replace={true} />;
  }

  return <RequestRoute {...rest} />;
};
